import React, { useState, useEffect } from 'react';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Mock data for users
    // In a real application, this would come from an API call
    const mockUsers = [
      {
        id: 1,
        name: 'Admin User',
        email: 'admin@edumanage.edu',
        role: 'admin',
        phone: '+1 (555) 123-4567',
        address: '123 Admin St, City, Country',
        isActive: true,
        createdAt: '2023-01-15',
        lastLogin: '2023-11-15 09:30:00',
        avatar: '/api/placeholder/40/40'
      },
      {
        id: 2,
        name: 'John Smith',
        email: 'john.smith@edumanage.edu',
        role: 'teacher',
        phone: '+1 (555) 234-5678',
        address: '456 Teacher Ave, City, Country',
        isActive: true,
        createdAt: '2023-02-20',
        lastLogin: '2023-11-15 10:15:00',
        avatar: '/api/placeholder/40/40'
      },
      {
        id: 3,
        name: 'Emma Johnson',
        email: 'emma.johnson@edumanage.edu',
        role: 'teacher',
        phone: '+1 (555) 345-6789',
        address: '789 Faculty Rd, City, Country',
        isActive: true,
        createdAt: '2023-03-10',
        lastLogin: '2023-11-14 14:20:00',
        avatar: '/api/placeholder/40/40'
      },
      {
        id: 4,
        name: 'Michael Brown',
        email: 'michael.brown@edumanage.edu',
        role: 'student',
        phone: '+1 (555) 456-7890',
        address: '321 Student Blvd, City, Country',
        isActive: true,
        createdAt: '2023-04-05',
        lastLogin: '2023-11-15 08:45:00',
        avatar: '/api/placeholder/40/40'
      },
      {
        id: 5,
        name: 'Sarah Davis',
        email: 'sarah.davis@edumanage.edu',
        role: 'student',
        phone: '+1 (555) 567-8901',
        address: '654 Learner Ln, City, Country',
        isActive: true,
        createdAt: '2023-05-12',
        lastLogin: '2023-11-13 16:30:00',
        avatar: '/api/placeholder/40/40'
      },
      {
        id: 6,
        name: 'Inactive User',
        email: 'inactive.user@edumanage.edu',
        role: 'student',
        phone: '+1 (555) 678-9012',
        address: '741 Inactive St, City, Country',
        isActive: false,
        createdAt: '2023-06-18',
        lastLogin: '2023-08-10 11:20:00',
        avatar: '/api/placeholder/40/40'
      }
    ];

    setTimeout(() => {
      setUsers(mockUsers);
      setLoading(false);
    }, 1000); // Simulate API call delay
  }, []);

  const filteredUsers = users.filter(user => {
    const matchesRole = !selectedRole || user.role === selectedRole;
    const matchesStatus = !selectedStatus || 
      (selectedStatus === 'active' ? user.isActive : !user.isActive);
    const matchesSearch = !searchTerm || 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesRole && matchesStatus && matchesSearch;
  });

  const toggleUserStatus = (userId) => {
    setUsers(users.map(user => 
      user.id === userId ? { ...user, isActive: !user.isActive } : user
    ));
  };

  const deleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(users.filter(user => user.id !== userId));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">User Management</h1>
          <button className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add User
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2">Search Users</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 pl-10 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Search by name or email..."
                />
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-2.5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Filter by Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="teacher">Teacher</option>
                <option value="student">Student</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Filter by Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img className="h-10 w-10 rounded-full" src={user.avatar} alt="" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">Joined {user.createdAt}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'teacher' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.lastLogin}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col space-y-2">
                        <button className="text-primary hover:text-blue-700 text-left">Edit</button>
                        <button 
                          onClick={() => toggleUserStatus(user.id)}
                          className={`${user.isActive ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'} text-left`}
                        >
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button 
                          onClick={() => deleteUser(user.id)}
                          className="text-red-600 hover:text-red-800 text-left"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">User Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Total Users</span>
                <span className="font-medium">{users.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Active Users</span>
                <span className="font-medium text-green-600">
                  {users.filter(u => u.isActive).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Inactive Users</span>
                <span className="font-medium text-red-600">
                  {users.filter(u => !u.isActive).length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Role Distribution</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Admins</span>
                <span className="font-medium text-purple-600">
                  {users.filter(u => u.role === 'admin').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Teachers</span>
                <span className="font-medium text-blue-600">
                  {users.filter(u => u.role === 'teacher').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Students</span>
                <span className="font-medium text-green-600">
                  {users.filter(u => u.role === 'student').length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span>Online Today</span>
                <span className="font-medium">
                  {users.filter(u => u.lastLogin.includes(new Date().toISOString().split('T')[0])).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>New This Week</span>
                <span className="font-medium">
                  {users.filter(u => {
                    const today = new Date();
                    const createdDate = new Date(u.createdAt);
                    const diffTime = Math.abs(today - createdDate);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays <= 7;
                  }).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Recently Active</span>
                <span className="font-medium">
                  {users.filter(u => {
                    const now = new Date();
                    const lastLoginDate = new Date(u.lastLogin.split(' ')[0]);
                    const diffTime = Math.abs(now - lastLoginDate);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays <= 30;
                  }).length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserList;